import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import HourglassTopTwoToneIcon from "@mui/icons-material/HourglassTopTwoTone";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import MicIcon from "@mui/icons-material/Mic";
import ReplyIcon from "@mui/icons-material/Reply";
import SendIcon from "@mui/icons-material/Send";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type React from "react";
import { useCallback, useContext, useRef, useState } from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import RecordingRequestHandler from "@/components/electron/RecordingRequestHandler";
import TemplateIcon from "@/components/Icons/TemplateIcon";
import { pastedFile } from "@/hooks/dispatch/composerPaste";
import { COMPOSER_MAX_CHARS, composerDraftSendable, composerTextTooLong } from "@/hooks/dispatch/composerSend";
import type { ReplyTrigger } from "@/hooks/dispatch/dispatchTracking";
import type { ComposerState } from "@/hooks/dispatch/useDispatchComposer";
import type { FileWithMetaType } from "@/hooks/UseTusHooks/Generics";
import TusHooksDropZone from "@/hooks/UseTusHooks/TusHooksDropZone";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import type { VoiceRecorder } from "@/hooks/useVoiceRecorder";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { zinc } from "@/ui/tokens";
import { redColor } from "@/utils/color";
import { pastableFileTypes } from "@/utils/pastableFileTypes";
import DispatchRecordingOverlay from "./DispatchRecordingOverlay";
import TemplateChoices, { toolPopoverPaperSx } from "./TemplateChoices";

/** DOM id of the row composer's text input, so the `reply` shortcut can focus it. */
export const DISPATCH_COMPOSER_INPUT_ID = "dispatchComposer";

/** DOM id of the side panel's composer input, which the `reply` shortcut focuses while History or Context is open. */
export const DISPATCH_PANEL_COMPOSER_INPUT_ID = "dispatchPanelComposer";

/** Which of the page's two composers this is; the frame and the input's size follow. */
export type ComposerVariant = "row" | "thread";

/** Which tool popover is open, if any. */
type Tool = "attach" | "templates";

const toolButtonSx = {
  color: zinc[400],
  p: 0.5,
  "&:hover": { color: "white" },
  "&.Mui-disabled": { color: zinc[600] },
} as const;

/**
 * The Dispatch page's message composer, shared by the selected row's inline reply and the side panel: a text box
 * with the driver list's affordances (a file, a template) in the page's own compact frame. Enter sends, Shift+Enter
 * breaks the line. The draft belongs to the caller (`useComposer`, or `useComposerState` over the page's drafts),
 * keyed per channel, so switching rows or threads keeps what was typed and attached. A file chosen or pasted waits as
 * a chip and uploads only when Send is pressed, as the classic composer's files do: the upload is tagged with the
 * channel, so the upload pipeline posts it the moment it lands, and nothing reaches the channel before Send. While
 * the send runs, the chip says the file is uploading. A template replaces the text. Given a `recorder`, a record
 * button sits between Templates and Send, and an overlay covers the box while a recording is on the go. Calling stays
 * with the driver list.
 *
 * @param composer - The draft and its send, from the caller's `useComposer` or `useComposerState`.
 * @param feedId - The channel the message goes to; null disables the box and says why.
 * @param variant - `row` for the one-to-three-line reply under a row, `thread` for the roomier box at the foot of the
 * side panel, under either tab.
 * @param inputId - A DOM id for the input, for a shortcut that focuses it.
 * @param placeholder - What the empty box says while it can send, over the variant's own wording.
 * @param testId - The root's test id; the tools inside carry their own, shared across composers.
 * @param sendTestId - The send button's test id.
 * @param recorder - The side panel's voice recorder; the row's reply has none, since moving the highlight changes its
 * channel without changing the URL a recording cancels on.
 */
export default function DispatchComposer({
  composer,
  feedId,
  variant,
  inputId,
  placeholder,
  testId,
  sendTestId,
  recorder,
}: {
  composer: ComposerState;
  feedId: string | null;
  variant: ComposerVariant;
  inputId?: string;
  placeholder?: string;
  testId: string;
  sendTestId: string;
  recorder?: VoiceRecorder;
}) {
  const { currentWorkspaceId: workspaceId } = useContext(WorkspaceContext);
  const { draft, onTextChange, onAttach, onDetach, send, sending, error, inputRef } = composer;
  const [tool, setTool] = useState<Tool | null>(null);
  const toolAnchor = useRef<HTMLDivElement>(null);
  const canReply = feedId != null;
  const tooLong = composerTextTooLong(draft);
  const canSend = canReply && !sending && composerDraftSendable(draft);
  const recordingOnTheGo = recorder != null && (recorder.recording || recorder.uploading || recorder.disabled);
  const shownError = error ?? recorder?.error?.message ?? null;

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    if (canSend) void send("keyboard");
  };

  // A pasted file the composer takes waits in the draft in place of the paste; anything else pastes as the browser
  // would.
  const onPaste = (event: React.ClipboardEvent) => {
    const file = pastedFile(event.clipboardData.files, pastableFileTypes());
    if (!file || !canReply || sending) return;
    event.preventDefault();
    onAttach({ file, name: file.name });
  };

  const closeTool = useCallback(() => {
    setTool(null);
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [inputRef]);

  // The drop zone hands back the file it read, renamed with an extension when it had none, with the details it read
  // alongside: the display name already cut to fit a chip, and a title and description for audio, which the upload
  // carries to the pipeline.
  const attached = useCallback(
    (data: FileWithMetaType | null) => {
      if (!data) return;
      onAttach({ file: data.file, name: data.meta.name, meta: data.meta });
      closeTool();
    },
    [onAttach, closeTool],
  );

  const shownPlaceholder = !canReply
    ? "No channel to write to"
    : (placeholder ?? (variant === "row" ? "Reply…" : "Start typing your message"));

  return (
    <Box data-sb={testId} sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Box
        ref={toolAnchor}
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 0.5,
          px: 1.25,
          py: variant === "row" ? 0.5 : 0.75,
          borderRadius: variant === "row" ? 1 : 2,
          position: "relative",
          bgcolor: zinc[800],
          border: `1px solid ${shownError ? redColor : zinc[700]}`,
          "&:focus-within": { borderColor: "var(--color-brand-primary-500)" },
        }}
      >
        {recorder && recordingOnTheGo && <DispatchRecordingOverlay recorder={recorder} />}
        {variant === "row" && <ReplyIcon sx={{ fontSize: 16, color: zinc[500], alignSelf: "center" }} />}
        {draft.attachment && (
          <Box
            data-sb={TestIds.dispatch.composerAttachment}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              alignSelf: "center",
              pl: 0.75,
              pr: 0.25,
              height: 24,
              maxWidth: 180,
              borderRadius: 1,
              bgcolor: zinc[700],
              color: zinc[100],
              fontSize: "0.75rem",
            }}
          >
            {sending ? (
              <HourglassTopTwoToneIcon sx={{ fontSize: 14, color: zinc[400], flex: "none" }} />
            ) : (
              <InsertDriveFileOutlinedIcon sx={{ fontSize: 14, color: zinc[400], flex: "none" }} />
            )}
            <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {sending ? "Uploading…" : draft.attachment.name}
            </Box>
            <IconButton size="small" aria-label="Remove file" onClick={onDetach} disabled={sending} sx={{ p: 0.25 }}>
              <CloseIcon sx={{ fontSize: 14, color: zinc[300] }} />
            </IconButton>
          </Box>
        )}
        <InputBase
          id={inputId}
          inputRef={inputRef}
          multiline
          minRows={1}
          maxRows={variant === "row" ? 3 : 6}
          value={draft.text}
          disabled={!canReply}
          onChange={(event) => onTextChange(event.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          placeholder={shownPlaceholder}
          inputProps={{ "aria-label": variant === "row" ? "Reply" : "Message" }}
          sx={{ flex: 1, minWidth: 0, py: 0.25, color: "white", fontSize: "0.875rem" }}
        />
        <Tooltip title="Attach a file">
          <IconButton
            size="small"
            aria-label="Attach a file"
            data-sb={TestIds.dispatch.composerAttach}
            disabled={!canReply || sending}
            onClick={() => setTool("attach")}
            sx={toolButtonSx}
          >
            <AttachFileIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Templates">
          <IconButton
            size="small"
            aria-label="Templates"
            data-sb={TestIds.dispatch.composerTemplates}
            disabled={!canReply || sending}
            onClick={() => setTool("templates")}
            sx={toolButtonSx}
          >
            <TemplateIcon width={16} height={16} />
          </IconButton>
        </Tooltip>
        {recorder && <RecordButton recorder={recorder} disabled={!canReply || sending} />}
        <IconButton
          aria-label="Send"
          data-sb={sendTestId}
          size="small"
          disabled={!canSend}
          onClick={() => void send("button")}
          // The arrow stays white and the button stays a button while nothing can be sent; only the brand color waits.
          sx={{
            color: "white",
            bgcolor: canSend ? "var(--color-brand-primary-600)" : zinc[600],
            "&:hover": { bgcolor: "var(--color-brand-primary-500)" },
            "&.Mui-disabled": { color: "white", bgcolor: zinc[600] },
          }}
        >
          <SendIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
      {(shownError || tooLong) && (
        <Typography sx={{ fontSize: "0.75rem", color: redColor }}>
          {shownError ?? `Message is over ${COMPOSER_MAX_CHARS} characters`}
        </Typography>
      )}
      <Popover
        open={tool === "attach"}
        anchorEl={toolAnchor.current}
        onClose={closeTool}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        slotProps={{
          paper: {
            "data-sb": TestIds.dispatch.composerAttachPopover,
            sx: toolPopoverPaperSx,
          } as React.ComponentProps<typeof Popover>["PaperProps"],
        }}
      >
        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: zinc[200], mb: 1 }}>Attach a file</Typography>
        {/* The file chosen here waits in the draft; it uploads once Send is pressed. */}
        <Box sx={{ color: zinc[200] }}>
          {tool === "attach" && (
            <TusHooksDropZone
              sx={{ position: "relative", maxHeight: "390px", overflowY: "auto" }}
              setUploadData={attached}
              confirmLabel="Attach"
            />
          )}
        </Box>
      </Popover>
      <Popover
        open={tool === "templates"}
        anchorEl={toolAnchor.current}
        onClose={closeTool}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        slotProps={{
          paper: {
            "data-sb": TestIds.dispatch.composerTemplatesPopover,
            sx: toolPopoverPaperSx,
          } as React.ComponentProps<typeof Popover>["PaperProps"],
        }}
      >
        {tool === "templates" && (
          <TemplateChoices
            workspaceId={workspaceId}
            onChoose={(text) => {
              onTextChange(text);
              closeTool();
            }}
          />
        )}
      </Popover>
    </Box>
  );
}

/**
 * The record button, styled as one of the composer's tools rather than a call to action: the size and grey of Attach
 * and Templates, bottom-aligned with them, so Send stays the one filled button in the row. A press starts a
 * recording, and the overlay then carries Cancel and Send; while the last recording processes or uploads the button
 * waits under an hourglass. Pressing it again while a recording runs stops and sends it, as the classic button does.
 *
 * Presses are single-flighted: starting a recording sets up the microphone before `recording` turns true, and a
 * second press landing in that window would otherwise start a second recording.
 */
function RecordButton({ recorder, disabled }: { recorder: VoiceRecorder; disabled: boolean }) {
  const start = useSingleFlight(recorder.start);
  const busy = recorder.processing || recorder.uploading;
  return (
    <RecordingRequestHandler>
      <Tooltip title="Record a voice message">
        <IconButton
          size="small"
          aria-label="Record a voice message"
          data-sb={TestIds.dispatch.composerRecord}
          disabled={disabled || recorder.disabled}
          onClick={() => void (recorder.recording ? recorder.stop() : start())}
          sx={toolButtonSx}
        >
          {busy ? <HourglassTopTwoToneIcon sx={{ fontSize: 18 }} /> : <MicIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Tooltip>
    </RecordingRequestHandler>
  );
}
